import type { StripeElementsOptions, StripePaymentElementOptions } from '@stripe/stripe-js';

/**
 * One look for the Stripe Payment Element, shared by the customer checkout and
 * the subscription pay page so the two never drift. This is the checkout's
 * long-standing appearance (flat theme, brand blue, uppercase labels) with the
 * accordion rows styled to match - the customer flow and the subscription flow
 * are meant to be visibly the same product.
 */
export const ELEMENTS_APPEARANCE: StripeElementsOptions['appearance'] = {
  theme: 'flat',
  variables: {
    colorPrimary: '#2f6188',
    colorBackground: '#ffffff',
    colorText: '#3c4952',
    colorDanger: '#c02929',
    fontFamily: "'Source Sans 3', -apple-system, sans-serif",
    borderRadius: '0px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': { border: '1px solid #c9ced2', boxShadow: 'none' },
    '.Input:focus': { border: '1px solid #2f6188', boxShadow: 'none', outline: 'none' },
    '.Label': {
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6d7880',
    },
    '.Tab': { border: '1px solid #dde1e4', boxShadow: 'none' },
    '.Tab--selected': { border: '1px solid #2f6188', boxShadow: 'none' },
    // The accordion rows, styled like the tabs were: a plain bordered tile that
    // turns brand-blue when picked.
    '.AccordionItem': { border: '1px solid #dde1e4', boxShadow: 'none' },
    '.AccordionItem--selected': { border: '1px solid #2f6188', boxShadow: 'none' },
  },
};

/**
 * The method list, as a compact accordion.
 *
 * `accordion` (not tabs) so any number of methods stack vertically instead of
 * overflowing into a broken-looking "+N" tab. `defaultCollapsed` keeps it short:
 * each method is a single icon-and-name row, one under another, and the form
 * opens only for the one the customer picks - so a merchant with many methods
 * enabled no longer makes the page scroll. `spacedAccordionItems` sets each row
 * apart as its own tile, which is the tidier look.
 */
export const PAYMENT_ELEMENT_LAYOUT: StripePaymentElementOptions['layout'] = {
  type: 'accordion',
  defaultCollapsed: true,
  radios: false,
  spacedAccordionItems: true,
};
